import React, {useCallback, useState, useMemo, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {getDateByCustomFormat, DATE_FORMATS} from 'utils/date';
import {useTranslation} from 'react-i18next';
import SortByCases from '../SortByCases';
import useUserStore from 'common/stores/user';
import useBmoStore from 'common/stores/bmoStore';

import SearchWidget from 'common/components/SearchWidget';
import {Layout, Table, Text, Checkbox} from '@phonectas/unifon-ui-kit';
import {StyledCenterCenterLayout} from 'common/styled';
import {useSmallScreen} from 'common/hooks/useScreenSize';
import {LIMIT_PAYLOAD} from 'services/constants';
import {debounce} from 'lodash';
import {BmoTicketsList} from 'common/types/store/bmoTypes';

interface SortValue {
    key: string;
    label: string;
    value: number[];
}

const CasesTable: React.FC = () => {
	const [t] = useTranslation();
	const user = useUserStore((state) => state.user);
	const fetchAllCases = useBmoStore((state) => state.fetchAllCases);
	const caseslist = useBmoStore((state) => state.ticketsList);
	const ticketId = useBmoStore((state) => state.ticketById);
	const hasMoreCases = useBmoStore((state) => state.hasMoreCases);
	const isLoading = useBmoStore((state) => state.isLoading);
	const hasFetchedCases = useRef<boolean>(false);
	const [query, setQuery] = useState<string>('');
	const DEFAULT_SORT = useMemo<SortValue>(() => ({
		key: 'open',
		label: t('Open'),
		value: [1, 3, 4, 5],
	}), [t]);
	const [sortByValue, setSortByValue] = useState<SortValue>(DEFAULT_SORT);
	const [myCases, setMyCases] = useState<boolean>(false);
	const currentId: number = parseInt(ticketId?.id as string);
	const navigate = useNavigate();
	const isSmallScreen = useSmallScreen('(max-width:1300px)');

	const debouncedFetchList = useMemo(() => debounce((searchValue: string) => {
		setQuery(searchValue);
	}, 500), [setQuery]);

	const onClick = useCallback((item: BmoTicketsList) => {
		navigate(`/BMO/cases?id=${item.id}`);
	}, [navigate]);

	const onToggleMyCases = useCallback(() => {
		setMyCases((prev) => !prev);
	}, []);

	const handleSortChange = useCallback((value: SortValue) => {
		setSortByValue(value);
	}, []);

	useEffect(() => () => debouncedFetchList.cancel(), [debouncedFetchList]);

	const clearSearch = useCallback(() =>{
		debouncedFetchList.cancel();
		setQuery('');
		setSortByValue(DEFAULT_SORT);
		setMyCases(false);
	}, [DEFAULT_SORT, debouncedFetchList]);

	const handleSearchChange = useCallback((value: string) => {
		if (typeof value === 'string') {
			debouncedFetchList(value);
		} else {
			setQuery('');
		}
	}, [debouncedFetchList]);

	// A search ignores the status filter, so paging must use the same filter as the first page.
	const onLoadMore = useCallback((size: number) => {
		if (!hasMoreCases || isLoading) return;
		fetchAllCases(
			size + LIMIT_PAYLOAD,
			query,
			query ? [] : sortByValue.value,
			myCases && user ? user.catalogID : '',
		);
	}, [fetchAllCases, query, sortByValue, myCases, user, hasMoreCases, isLoading]);

	useEffect(() => {
		if (query) {
			fetchAllCases(LIMIT_PAYLOAD, query, [], myCases && user ? user.catalogID : '');
		} else {
			fetchAllCases(
				LIMIT_PAYLOAD,
				query,
				sortByValue.value,
				myCases && user ? user.catalogID : '',
			);
		}
	}, [fetchAllCases, query, sortByValue, myCases, user]);

	const isDataInFilter = caseslist?.length === 0;

	const renderItem = useCallback((item: BmoTicketsList, index: number) => (
		<Table.Row
			key={item.id}
			index={index}
			onClick={() => onClick(item)}
			selected={item.id === currentId}
		>
			<Table.Cell>{item?.id}</Table.Cell>
			<Table.Cell>{item?.status}</Table.Cell>
			<Table.Cell>{item?.reporter}</Table.Cell>
			<Table.Cell>
				<Text truncate={2}>
					{item?.unifon_subject}
				</Text>
			</Table.Cell>
			<Table.Cell>{item?.zendeskRef}</Table.Cell>
			<Table.Cell>{getDateByCustomFormat(item?.createdAt, DATE_FORMATS.DD_MM_YYYY)}</Table.Cell>
		</Table.Row>
	), [onClick, currentId]);

	// Deep link (?id=) to a case outside the first page: search for it once, on first load only,
	// so later searches by the user are never overridden. Shown in the search box so list and controls agree.
	useEffect(() => {
		if (hasFetchedCases.current || !ticketId?.id || !caseslist) return;
		hasFetchedCases.current = true;
		if (!caseslist.some((item) => item.id === currentId)) {
			setQuery(ticketId.id);
		}
	}, [ticketId, caseslist, currentId]);

	return (
		<Layout fullHeight fullWidth>
			<Layout
				direction="row"
				fullWidth
				justifyContent="end"
				gap="xxl"
				alignItems="center"
			>
				<Layout direction="column">
					<SortByCases
						sortByValue={sortByValue}
						onSort={handleSortChange}
					/>
				</Layout>
				<Layout direction="column" justifyContent="start">
					{
						user?.name && (
							<Checkbox
								label={t('My cases')}
								toggle
								big
								checked={myCases}
								onChange={onToggleMyCases}
							/>
						)
					}
				</Layout>
				<Layout direction="column" style={{width: '250px'}}>
					<SearchWidget
						onChange={handleSearchChange}
						placeholder={t('Search...')}
						clearSearch={clearSearch}
						searchQuery={query}
						value={query}
						fullWidth
					/>
				</Layout>
			</Layout>
			<Layout fullWidth nowrap fullHeight>
				{
					isDataInFilter ?
						<StyledCenterCenterLayout>
							<Text variant="h4">
								{t('No cases available in this category')}
							</Text>
						</StyledCenterCenterLayout> :
						<Layout
							grow
							fullWidth
							fullHeight
							direction="column"
							nowrap
							style={{paddingBottom: '20px'}}
						>
							<Table
								items={caseslist || []}
								renderItem={(item, index) => renderItem(item, index)}
								virtualized
								fullHeight
								onScrollDown={onLoadMore}
							>
								<Table.Row>
									<Table.Cell width={isSmallScreen ? 90 : 0}>{t('ID')}</Table.Cell>
									<Table.Cell width={isSmallScreen ? 110 : 0}>{t('status')}</Table.Cell>
									<Table.Cell>{t('Reporter')}</Table.Cell>
									<Table.Cell>{t('Subject')}</Table.Cell>
									<Table.Cell width={isSmallScreen ? 85 : 0}>{t('zendesk ref')}</Table.Cell>
									<Table.Cell width={isSmallScreen ? 95 : 0}>{t('Created at')}</Table.Cell>
								</Table.Row>
							</Table>
						</Layout>
				}
			</Layout>
		</Layout>
	);
}

export default React.memo(CasesTable);
